import {
  CAMPAIGN_SANDBOX_ROLE_DM,
  CAMPAIGN_SANDBOX_ROLE_PLAYER,
  CAMPAIGN_SANDBOX_SOURCE_BLANK,
  CAMPAIGN_SANDBOX_SOURCE_DEMO,
  CAMPAIGN_SANDBOX_SOURCE_CAMPAIGN,
  CAMPAIGN_SANDBOX_SOURCE_FORGE_PREVIEW,
} from './campaignSandboxContract';
import {
  buildBlankCampaignSandboxSeed,
  buildCampaignSandboxCharacterId,
  buildDemoCampaignSandboxSeed,
  normalizeCampaignSandboxSeedId,
} from './campaignSandboxSeedBuilders';

const truthyParam = (value) => (
  value === ''
  || value === '1'
  || value === 'true'
  || value === 'yes'
  || value === 'demo'
  || value === 'blank'
);

const normalizeSandboxRouteSource = (value) => {
  if (value === CAMPAIGN_SANDBOX_SOURCE_BLANK) return CAMPAIGN_SANDBOX_SOURCE_BLANK;
  if (value === CAMPAIGN_SANDBOX_SOURCE_CAMPAIGN) return CAMPAIGN_SANDBOX_SOURCE_CAMPAIGN;
  if (value === CAMPAIGN_SANDBOX_SOURCE_FORGE_PREVIEW) return CAMPAIGN_SANDBOX_SOURCE_FORGE_PREVIEW;
  return CAMPAIGN_SANDBOX_SOURCE_DEMO;
};

const parseSearchParams = (search = '') => {
  const raw = typeof search === 'string' ? search : '';
  return new URLSearchParams(raw.startsWith('?') ? raw : `?${raw}`);
};

const SANDBOX_INITIAL_VIEW_ALLOWLIST = new Set([
  'dm',
  'home',
  'combat',
  'shop',
  'loot',
  'crafting',
  'travel',
  'journal',
  'npcs',
  'timeline',
]);

const SANDBOX_DM_ONLY_INITIAL_VIEWS = new Set(['dm', 'timeline']);
const SANDBOX_PLAYER_ONLY_INITIAL_VIEWS = new Set(['home']);

const normalizeInitialView = (value, sandboxRole = CAMPAIGN_SANDBOX_ROLE_DM) => {
  if (!SANDBOX_INITIAL_VIEW_ALLOWLIST.has(value)) return null;
  if (sandboxRole === CAMPAIGN_SANDBOX_ROLE_PLAYER && SANDBOX_DM_ONLY_INITIAL_VIEWS.has(value)) {
    return null;
  }
  if (sandboxRole === CAMPAIGN_SANDBOX_ROLE_DM && SANDBOX_PLAYER_ONLY_INITIAL_VIEWS.has(value)) {
    return null;
  }
  return value;
};

const normalizeSandboxPersonaKey = (sandboxId, value, fallback) => {
  const raw = String(value || fallback || '').trim();
  const normalizedSandboxId = normalizeCampaignSandboxSeedId(sandboxId);
  const fullIdPrefix = `sandbox-${normalizedSandboxId}-persona-`;
  if (raw.startsWith(fullIdPrefix)) return raw.slice(fullIdPrefix.length) || fallback;
  return raw || fallback;
};

const normalizeDemoPartySizeForPlayer = (personaKey, partySize) => {
  const parsed = Number(partySize);
  if (!Number.isFinite(parsed)) return undefined;
  const wanted = String(personaKey || '').trim().toLowerCase();
  const requiredIndex = wanted === 'ventyl' ? 2
    : wanted === 'eve' ? 3
      : wanted === 'khan' ? 4
        : 1;
  return Math.max(parsed, requiredIndex);
};

export const buildCampaignSandboxSeedFromSearch = (search = '') => {
  const params = parseSearchParams(search);
  if (!params.has('campaignSandbox')) return null;
  const sandboxParam = params.get('campaignSandbox') || '';
  if (!truthyParam(sandboxParam)) return null;

  const sourceParam = params.get('sandboxSource') || params.get('source') || sandboxParam;
  const sourceMode = normalizeSandboxRouteSource(sourceParam);
  const sandboxId = normalizeCampaignSandboxSeedId(
    params.get('sandboxId')
    || params.get('id')
    || (sourceMode === CAMPAIGN_SANDBOX_SOURCE_BLANK ? 'blank-preview' : 'demo-preview'),
  );
  const sandboxRole = params.get('role') === CAMPAIGN_SANDBOX_ROLE_PLAYER
    ? CAMPAIGN_SANDBOX_ROLE_PLAYER
    : CAMPAIGN_SANDBOX_ROLE_DM;
  const defaultPersonaKey = sourceMode === CAMPAIGN_SANDBOX_SOURCE_BLANK ? 'sandbox-character' : 'lika';
  const personaKey = normalizeSandboxPersonaKey(
    sandboxId,
    params.get('persona') || params.get('char'),
    defaultPersonaKey,
  );
  const sandboxPersonaId = sandboxRole === CAMPAIGN_SANDBOX_ROLE_PLAYER
    ? buildCampaignSandboxCharacterId({ sandboxId, personaKey })
    : null;
  const personaCount = params.has('partySize')
    ? (sandboxRole === CAMPAIGN_SANDBOX_ROLE_PLAYER && sourceMode === CAMPAIGN_SANDBOX_SOURCE_DEMO
      ? normalizeDemoPartySizeForPlayer(personaKey, params.get('partySize'))
      : Number(params.get('partySize')))
    : undefined;
  const initialView = normalizeInitialView(params.get('view') || null, sandboxRole);

  const common = {
    sandboxId,
    sandboxRole,
    sandboxPersonaId,
    initialView,
  };
  if (
    sourceMode === CAMPAIGN_SANDBOX_SOURCE_BLANK
    || sourceMode === CAMPAIGN_SANDBOX_SOURCE_CAMPAIGN
    || sourceMode === CAMPAIGN_SANDBOX_SOURCE_FORGE_PREVIEW
  ) {
    return buildBlankCampaignSandboxSeed({
      ...common,
      sourceMode,
      name: sourceMode === CAMPAIGN_SANDBOX_SOURCE_FORGE_PREVIEW
        ? 'Campaign Sandbox Preview'
        : 'Blank Campaign Sandbox',
      sandboxPersonaName: personaKey,
    });
  }
  return buildDemoCampaignSandboxSeed({
    ...common,
    name: 'Demo Campaign Sandbox',
    personaCount,
  });
};

export const buildCampaignSandboxDirectUrl = (seed = {}, currentLocation = null) => {
  if (!seed?.sandboxId) return null;
  const pathname = currentLocation?.pathname || '/';
  const params = new URLSearchParams();
  params.set('campaignSandbox', '1');
  params.set('sandboxId', seed.sandboxId);
  params.set('source', normalizeSandboxRouteSource(seed.sourceMode));
  const sandboxRole = seed.launchTarget?.sandboxRole || seed.initialLaunch?.role;
  if (sandboxRole === CAMPAIGN_SANDBOX_ROLE_PLAYER) {
    params.set('role', CAMPAIGN_SANDBOX_ROLE_PLAYER);
    const personaId = seed.launchTarget?.sandboxPersonaId || seed.initialLaunch?.charId;
    const personaParam = normalizeSandboxPersonaKey(seed.sandboxId, personaId, null);
    if (personaParam) params.set('persona', personaParam);
  }
  const initialView = seed.launchTarget?.initialView || seed.initialLaunch?.initialView;
  const normalizedInitialView = normalizeInitialView(initialView, sandboxRole);
  if (normalizedInitialView) params.set('view', normalizedInitialView);
  return `${pathname}?${params.toString()}`;
};
